import { useContext } from 'react';
import { Row } from '@tanstack/react-table';
import TableContext from '../TableContext';
import TableBodyLoading from './TableBodyLoading';
import TableEmptyState from './TableEmptyState';
import { TableBodyStyled } from './TableBody.styles';
import TableBodyCell from './TableBodyCell';

interface TableBodyProps<T> {
  className?: string;
  handleRowClick?: (
    e: React.MouseEvent<HTMLTableRowElement>,
    row: Row<T>,
  ) => void;
  getRowClassName?: (row: Row<T>) => string | undefined;
}

const TableBody = <T,>({
  className,
  handleRowClick,
  getRowClassName,
}: TableBodyProps<T>) => {
  const { isLoading, tableInstance } = useContext(TableContext);
  const { rows } = tableInstance.getRowModel();
  const isEmpty = rows.length === 0;

  if (isLoading) {
    return (
      <TableBodyStyled className={className}>
        <TableBodyLoading />
      </TableBodyStyled>
    );
  }

  return (
    <TableBodyStyled
      className={className}
      isClickable={Boolean(handleRowClick)}
    >
      {isEmpty && <TableEmptyState />}
      {rows.map((row) => (
        <tr
          key={row.id}
          className={getRowClassName?.(row)}
          onClick={(e) => handleRowClick?.(e, row)}
        >
          {row.getVisibleCells().map((cell) => (
            <TableBodyCell cell={cell} key={cell.id} />
          ))}
        </tr>
      ))}
    </TableBodyStyled>
  );
};

TableBody.defaultProps = {
  className: '',
  handleRowClick: undefined,
  getRowClassName: undefined,
};

export default TableBody;
